import type { JourneyHttpScore, StepHandle } from '@servicetitan/journey';

/**
 * Score for a request that Apollo's `retryLink` retries. A 5xx or network
 * error on one attempt does not fail the step, because a later attempt can
 * still succeed; the step fails instead when the call finally throws. Other
 * responses keep the default scoring, so a slow attempt still counts.
 */
export const ignoreRetriedFailures: JourneyHttpScore = ({ status }) =>
  status === undefined || status >= 500 ? 'continue' : undefined;

/**
 * Score for a request where no 4xx is an expected answer. A 4xx fails the
 * step like a 5xx does; everything else keeps the default scoring.
 */
export const failOnClientErrors: JourneyHttpScore = ({ status }) =>
  status !== undefined && status >= 400 && status < 500 ? 'bad' : undefined;

/**
 * `fetch` init options that bind a request to a journey step, so the journey
 * scores its status and latency. Pass them to `fetch` or as Apollo
 * `context.fetchOptions`. `@servicetitan/journey` reads `stamp` off the init
 * and `fetch` ignores it. `undefined` without a step or for a no-op step.
 *
 * @param score - Overrides how the journey scores this request.
 */
function stampedFetchOptions(
  step?: StepHandle,
  score?: JourneyHttpScore,
): RequestInit | undefined {
  const stamp = step?.stamp(score);
  return stamp ? ({ stamp } as RequestInit) : undefined;
}

export default stampedFetchOptions;
